import React, { useState, useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { motion, useScroll, useTransform } from 'framer-motion';

gsap.registerPlugin(ScrollTrigger);

const WhatIDo = ({ darkMode = true }) => {
  const [hoveredItem, setHoveredItem] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  
  // Create refs
  const sectionRef = useRef(null);
  const containerRef = useRef(null);
  const linesRef = useRef(null);
const services = [
  {
    title: 'Full Stack Web Development',
    description: 'I build dynamic web applications using React, Next.js, and Python or Node.js for the backend—ensuring scalable, maintainable, and clean architecture.'
  },
  {
    title: 'Backend Development & APIs',
    description: 'I develop RESTful APIs and backend logic using Express or Flask, integrating databases and third-party services to deliver secure, high-performance functionality.'
  },
  {
    title: 'Database Design & Management',
    description: 'I work with relational (PostgreSQL, MySQL) and NoSQL (MongoDB) databases—designing schemas, writing optimized queries, and ensuring data integrity.'
  },
  {
    title: 'Data Handling & Analysis',
    description: 'I use Python to process, analyze, and visualize data, automating workflows and uncovering insights that inform product and business decisions.'
  },
  {
    title: 'Performance & Clean Code',
    description: 'I write efficient, modular code and apply performance best practices like lazy loading, caching, and SSR in Next.js to create fast, responsive experiences.'
  }
];

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"]
  });
    
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.9, 1], [0, 1, 1, 0]);
  const yOffset = useTransform(scrollYProgress, [0, 0.2, 0.9, 1], [100, 0, 0, 100]);

  useEffect(() => {
    const lines = linesRef.current;
    
    // Set up intersection observer for scroll animations
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );
    
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    
    // GSAP Animation
    if (lines && containerRef.current) {
      gsap.to(lines, {
        opacity: 1,
        stagger: 0.2,
        scrollTrigger: {
          trigger: containerRef.current,
          start: 'top center',
          end: 'bottom center',
          scrub: true,
          markers: false
        }
      });
    }
    
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
      setIsVisible(false);
    };
  }, []);

  return (
    <div 
      id="services"  
      ref={sectionRef} 
      className={`py-20 w-full overflow-x-hidden ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-800'}`}
    >
      <div 
        ref={containerRef}
        className="w-full overflow-x-hidden"
      >
        {/* Title and Subtitle - Now centered */}
        <motion.div 
          className="mb-12 text-center relative px-4"
          style={{ opacity, y: yOffset }}
        >
          <motion.h2 
            className="text-6xl md:text-8xl font-black mb-3 tracking-tight tracking-wider text-center"
            initial={{ clipPath: "polygon(0 0, 0 0, 0 100%, 0 100%)" }}
            whileInView={{ clipPath: "polygon(0 0, 100% 0, 100% 100%, 0 100%)" }}
            viewport={{ once: true }}
            transition={{ duration: 1, ease: "easeOut" }}
          >
            <div className={`${darkMode ? 'text-white' : 'text-gray-900'} tracking-[0.2em] font-light mb-0`}>
              WHAT I DO
            </div>
          </motion.h2>
        </motion.div>
        
        <div className="w-full overflow-x-hidden" ref={linesRef}>
          {services.map((service, index) => (
            <div 
              key={index}
              className="relative group w-full"
              onMouseEnter={() => setHoveredItem(index)}
              onMouseLeave={() => setHoveredItem(null)}
            >
              {/* Desktop View (Hover Effect) */}
              <div className="hidden md:block">
                <div 
                  className={`
                    flex items-center w-full relative
                    ${darkMode ? 'border-white/20' : 'border-black/20'}
                    py-8
                    transition-all duration-300 ease-in-out
                    ${hoveredItem === index 
                      ? (darkMode 
                        ? 'bg-yellow-100 text-black' 
                        : 'bg-blue-100 text-black')
                      : (darkMode 
                        ? 'bg-gray-900 text-white' 
                        : 'bg-white text-black')}
                  `}
                >
                  <div className="text-5xl font-regular pl-16  flex-grow px-4 relative z-10">
                    {service.title}
                  </div>
                  <div 
                    className={`
                      absolute right-0 top-0 bottom-0 flex items-center px-4 
                      transition-all duration-300 ease-in-out
                      ${hoveredItem === index 
                        ? 'w-1/2 opacity-100' 
                        : 'w-0 opacity-0'}
                      overflow-hidden
                    `}
                  >
                    {service.description}
                  </div>
                </div>
              </div>

              {/* Mobile View (Always Visible Description) */}
              <div className="md:hidden">
                <div className={`
                  w-full
                  border-b 
                  ${darkMode ? 'border-white/20' : 'border-black/20'} 
                  pb-6 px-4
                `}>
                  <div className="text-4xl font-bold mb-4">
                    {service.title}
                  </div>
                  <div className={`${darkMode ? 'text-white/80' : 'text-black/80'}`}>
                    {service.description}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default WhatIDo;